import store from '@/store/store'
import axios from 'axios'
import { vue } from '@/main.js'
import router from '@/router.js'

/**
 * Exibe apenas o último toast de erro.
 *
 * @param {Vue} vue Instância do Vue
 * @param {string} message Mensagem do erro
 * @param {string} title Título do toast
 * @return {void}
 */
export function showLastErrorToast(vue, message, title = 'Erro') {
  const toastsAnteriores = document.querySelectorAll('.iziToast')

  toastsAnteriores.forEach((toast) => {
    vue.$toast.hide({}, toast)
  })

  vue.$toast.error(message, title, {
    position: 'topRight',
    timeout: 5000,
    closeOnClick: true,
  })
}

const api = axios.create({
  baseURL: 'https://esusfeedback.com.br/api',
})
api.interceptors.request.use(
  function (config) {
    config.headers.authorization = 'Bearer ' + store.state.accessToken

    return config
  },
  function (error) {
    // Do something with request error
    return Promise.reject(error)
  }
)
api.interceptors.response.use(
  (response) => {
    if (response.status === 200 || response.status === 201) {
      return Promise.resolve(response)
    }

    return Promise.reject(response)
  },
  (error) => {
    if (axios.isCancel(error)) {
      return Promise.reject(error)
    }

    const shouldShowToast = !error.config?.url?.includes(
      'profissional-pec-cnes-datasus'
    )

    if (error.response !== undefined) {
      const status = error.response.status
      const message = error.response.data?.message

      if (status === 401 || status === 403) {
        store.dispatch('logout')
        vue.$router.push('/login')

        if (message === 'Expired token') {
          showLastErrorToast(
            vue,
            'Sessão expirada! Por favor, faça login novamente.',
            'Atenção'
          )
        }

        return Promise.reject(error)
      }

      if (error.config.returnReject) {
        return Promise.reject(error)
      }

      if (!shouldShowToast) {
        return Promise.reject(error)
      }

      if (message) {
        showLastErrorToast(vue, message)
      } else {
        showLastErrorToast(
          vue,
          'Ocorreu um erro inesperado. Tente novamente mais tarde ou entre em contato com o suporte.'
        )
      }

      return Promise.reject(error)
    }

    // Sem resposta do servidor
    if (error.request) {
      if (shouldShowToast) {
        showLastErrorToast(
          vue,
          'Não foi possível se conectar ao servidor. Tente novamente mais tarde ou entre em contato com o suporte.'
        )
      }
    } else {
      if (shouldShowToast) {
        showLastErrorToast(
          vue,
          'Ocorreu um erro inesperado. Tente novamente mais tarde ou entre em contato com o suporte.'
        )
      }
    }

    return Promise.reject(error)
  }
)

const apiPublic = axios.create({
  baseURL: 'https://esusfeedback.com.br/api',
})

apiPublic.interceptors.response.use(
  (response) => {
    if (response.status === 200 || response.status === 201) {
      return Promise.resolve(response)
    }

    return Promise.reject(response)
  },
  (error) => {
    if (axios.isCancel(error)) {
      return Promise.reject(error)
    }

    // O servidor respondeu com erro
    if (error.response !== undefined) {
      const message = error.response.data?.message

      if (error.config.returnReject) {
        return Promise.reject(error)
      }

      if (message) {
        showLastErrorToast(vue, message)
      } else {
        showLastErrorToast(
          vue,
          'Ocorreu um erro inesperado. Tente novamente mais tarde ou entre em contato com o suporte.'
        )
      }

      return Promise.reject(error)
    }

    // A requisição foi realizada, mas o servidor não respondeu
    if (error.request) {
      showLastErrorToast(
        vue,
        'Não foi possível se conectar ao servidor. Tente novamente mais tarde ou entre em contato com o suporte.'
      )
    } else {
      // Erro ao configurar/processar a requisição
      showLastErrorToast(
        vue,
        'Ocorreu um erro inesperado. Tente novamente mais tarde ou entre em contato com o suporte.'
      )
    }

    return Promise.reject(error)
  }
)

/**
 * API autenticada exclusiva das rotas protegidas do PEP.
 */
const apiPep = axios.create({
  baseURL: 'https://esusfeedback.com.br/api',
})

apiPep.interceptors.request.use(
  (config) => {
    const token = store.getters['pep/token']

    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    return config
  },
  (error) => Promise.reject(error)
)

apiPep.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (axios.isCancel(error)) {
      return Promise.reject(error)
    }

    if (error.response) {
      const status = error.response.status

      if (status === 401 || status === 403) {
        await store.dispatch('pep/logout')
        // vue.$router.push('/login')
      }
    }

    return Promise.reject(error)
  }
)

export { api, apiPublic, apiPep }
